import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  ComplianceProgramAppliesTo,
  GetComplianceProgramParticipantsPreviewQuery,
  GetComplianceProgramParticipantsPreviewQueryVariables,
} from "types/graphql-schema";

import getComplianceProgramParticipantsPreviewQuery from "@apps/programs/graphql/get-compliance-program-participants-preview-query";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";

import { convertProgramFormToMutationVariables } from "../form-data";
import { ProgramForm } from "../types";
import { missingSelectionField } from "../use-program-form-timeline";

/**
 * Above this many people the list gets a search box. Below it the whole list is on
 * screen already, so a box to find one row in five would be in the way.
 */
const searchFromLength = 5;

const people = (count: number) =>
  `${count} ${count === 1 ? "person" : "people"}`;

type Preview = NonNullable<
  GetComplianceProgramParticipantsPreviewQuery["complianceProgramParticipantsPreview"]
>;

const ParticipantRow = ({ row }: { row: Preview["participants"][number] }) => (
  <li className="flex items-center justify-between gap-2 py-1">
    <div className="flex min-w-0 items-center gap-2">
      <Avatar user={row.user} size="5" />
      <span className="truncate text-sm">{row.user.name}</span>
    </div>
    <span className="shrink-0 text-xs text-gray-400">
      {row.manager ? row.manager.name : "No manager"}
    </span>
  </li>
);

const ParticipantWarning = ({
  warning,
}: {
  warning: Preview["warnings"][number];
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span>{warning.message}</span>
        {warning.users.length > 0 && (
          <button
            type="button"
            className="text-xs underline"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide" : "Who?"}
          </button>
        )}
      </div>
      {expanded && (
        <ul className="mt-1 text-xs">
          {warning.users.map((user) => (
            <li key={user.id}>{user.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Who the participant rule on this form resolves to, right now.
 *
 * Asks the backend, because the rule is resolved by the same code that will write
 * the roster on publish: "all managers", "all my reports" and the hire-date cutoff
 * cannot be worked out in the browser, and a rule that guesses differently from the
 * publish would be worse than no preview at all.
 *
 * Closed until asked for. Resolving a rule walks the whole organization's
 * memberships, so an admin who only came to change one exclusion should not pay for
 * it, let alone once per edit. Opening the panel is the admin saying they want the
 * answer; until then the query does not run at all.
 *
 * Nothing here saves. The query takes the form's current values as arguments and
 * hands back an answer; the review is written only when the admin saves the form.
 */
const ProgramFormParticipantsPreview = ({
  proposedComplianceProgram,
}: {
  proposedComplianceProgram: ProgramForm;
}) => {
  const label = useLabel();
  const [open, setOpen] = useState(false);

  // The values the query needs, and only those, so that typing a title on another
  // tab does not restart the debounce timer.
  const variables =
    useMemo<GetComplianceProgramParticipantsPreviewQueryVariables>(() => {
      const saved = convertProgramFormToMutationVariables(
        proposedComplianceProgram,
        true,
      );
      return {
        organizationId: proposedComplianceProgram.organizationId,
        complianceProgramId: proposedComplianceProgram.id,
        // The steps and the templates decide which messages the review sends, which
        // is what turns "this person has no manager" into a warning or into nothing.
        steps: saved.steps,
        assessmentTemplates: saved.assessmentTemplates,
        calibration: saved.calibration,
        ongoing: saved.ongoing,
        startDateOffset: saved.startDateOffset,
        appliesTo: saved.appliesTo,
        appliesToTeams: saved.appliesToTeams,
        appliesToManagers: saved.appliesToManagers,
        appliesToUsers: saved.appliesToUsers,
        excludedUsers: saved.excludedUsers,
        excludedHireDateAfter: saved.excludedHireDateAfter,
        // A cleared date and an untouched one both serialize to nothing, so the
        // save conversion's flag says which. Without it the backend keeps planning
        // around the cutoff the draft still has saved.
        removeExcludedHireDateAfter: saved.removeExcludedHireDateAfter,
      };
    }, [proposedComplianceProgram]);

  // Debounced on the values rather than on the object, because every keystroke
  // anywhere in the form hands this component a new object with the same values.
  const liveKey = JSON.stringify(variables);
  const debouncedKey = useDebounce(liveKey, 500);
  const debouncedVariables = useMemo(
    () =>
      JSON.parse(
        debouncedKey,
      ) as GetComplianceProgramParticipantsPreviewQueryVariables,
    [debouncedKey],
  );

  const missingField =
    missingSelectionField[proposedComplianceProgram.appliesTo];
  const waitingForASelection = Boolean(
    missingField &&
      (proposedComplianceProgram[missingField] as unknown[]).length === 0,
  );

  const { data, loading, error } = useQuery<
    GetComplianceProgramParticipantsPreviewQuery,
    GetComplianceProgramParticipantsPreviewQueryVariables
  >(getComplianceProgramParticipantsPreviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: debouncedVariables,
    skip: !open || waitingForASelection,
  });

  // Closing the panel sets `skip`, and Apollo clears `data` when it does, so the
  // answer has to be kept here for the closed header to keep the count. Kept with
  // the selections it was an answer to, so a count is never shown beside
  // selections it does not describe.
  const [remembered, setRemembered] = useState<{
    key: string;
    preview: Preview;
  } | null>(null);
  const fromServer = data?.complianceProgramParticipantsPreview;
  useEffect(() => {
    if (fromServer) {
      setRemembered({ key: debouncedKey, preview: fromServer });
    }
  }, [fromServer, debouncedKey]);

  const [search, setSearch] = useState("");
  const preview =
    fromServer ??
    (remembered?.key === liveKey ? remembered.preview : undefined);
  const participants = preview?.participants ?? [];
  // A query typed against a long list must not keep filtering a short one after the
  // selections change and the box goes away, which would hide rows with nothing on
  // screen to explain why.
  const searchable = participants.length > searchFromLength;
  const query = searchable ? search.trim().toLowerCase() : "";
  // The whole list is always on screen, so this only narrows what is visible. The
  // count in the header stays the real one, because that is what gets published.
  const listed = query
    ? participants.filter((row) => row.user.name.toLowerCase().includes(query))
    : participants;

  const emptyStateMessage = () => {
    if (waitingForASelection) {
      if (
        proposedComplianceProgram.appliesTo ===
        ComplianceProgramAppliesTo.Departments
      ) {
        return `Pick ${label("team", { pluralize: true })} above to see who takes part.`;
      }
      if (
        proposedComplianceProgram.appliesTo ===
        ComplianceProgramAppliesTo.ReportsTo
      ) {
        return "Pick managers above to see who takes part.";
      }
      return "Pick employees above to see who takes part.";
    }
    return "This rule matches nobody right now. Change it so somebody takes part before you publish.";
  };

  return (
    <div className="mt-6 rounded-md border p-6">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4"
        // Spelled out, because the visible text is two spans and a count, which
        // would otherwise run together into one unreadable name.
        aria-label="Preview who takes part"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-baseline gap-2">
          <span className="tf-header-base-dark">Preview</span>
          {/* Kept in the closed header once it is known, so collapsing the panel
              does not throw the answer away. */}
          {preview && (
            <span className="text-sm text-gray-500">
              {people(preview.participantCount)}
            </span>
          )}
        </span>
        <span className="shrink-0 text-sm text-blue-600 underline">
          {open ? "Hide" : "Show who takes part"}
        </span>
      </button>

      {/* A failed request must never read as "nobody takes part", and must never
          leave an old list looking freshly checked. Shown whether or not there is
          something to show under it. */}
      {open && error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
          {preview
            ? "This list could not be refreshed, so it may be out of date. Reload the page to try again."
            : "These participants could not be loaded. Reload the page to try again."}
        </div>
      )}

      {open && loading && !preview && (
        <div className="pt-3">
          <Loading size="5" mini />
        </div>
      )}

      {open && preview && preview.participantCount > 0 && (
        <div className="flex flex-col gap-3 pt-3">
          {loading && <Loading size="4" mini />}

          {preview.warnings.length > 0 && (
            <div className="flex flex-col gap-2">
              {preview.warnings.map((warning) => (
                <ParticipantWarning key={warning.reason} warning={warning} />
              ))}
            </div>
          )}

          {searchable && (
            <div className="flex items-baseline gap-2">
              <input
                type="search"
                className="w-full rounded-md border px-2 py-1 text-sm"
                aria-label="Search the participants"
                placeholder="Search by name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {query && (
                <span className="shrink-0 text-xs text-gray-500">
                  {listed.length} of {participants.length}
                </span>
              )}
            </div>
          )}

          {/* Everybody is listed. The box scrolls rather than the page, so a
              140-person review does not push the rest of the form out of reach. */}
          <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
            {listed.map((row) => (
              <ParticipantRow key={row.user.id} row={row} />
            ))}
          </ul>
          {listed.length === 0 && (
            <div className="text-sm text-gray-500">
              Nobody in this list matches “{search.trim()}”.
            </div>
          )}
        </div>
      )}

      {open &&
        !loading &&
        !error &&
        (waitingForASelection || !preview?.participantCount) && (
          <div className="pt-3 text-sm text-gray-500">
            {emptyStateMessage()}
          </div>
        )}
    </div>
  );
};

export default ProgramFormParticipantsPreview;
